import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { IconCheck, type Icon } from "@tabler/icons-react";
import { cn } from "../../lib/utils";
import { POPOVER_CARD_CLASS, PopoverCard, type PopoverAnchor } from "./Popover";

/**
 * The one dropdown menu of the desktop app (Figma "Menu · compte", the
 * sidebar's account menu): a card of rows hung off a trigger. Every desktop
 * menu draws its rows with `DesktopMenuItem`, so the account menu, a
 * conversation's actions and a sort picker read as the same control.
 *
 * The caller owns the trigger and the open state (`usePopoverMenu`), and the
 * card's width when the rows should not set it.
 */
export function DesktopMenu({
  anchor,
  className,
  children,
}: {
  anchor: PopoverAnchor;
  className: string | null;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <PopoverCard
      anchor={anchor}
      role="menu"
      className={cn("gap-0.5 rounded-xl border-ds-slate-100 p-1.5", className)}
    >
      {children}
    </PopoverCard>
  );
}

/**
 * One row of a `DesktopMenu`. `trailing` is what the row ends on: a hint (a
 * shortcut, the current value of a setting), or the check of the option in
 * use. A row is a button, or a link where it only navigates. A disabled row
 * stays in the menu, greyed out, where its action is unavailable for now.
 * `description` is a second line under the label, for a row whose effect
 * reaches past the menu (Figma "D3.explore.room.menu.pin": Pin to top).
 */
export function DesktopMenuItem({
  icon,
  label,
  description,
  trailing,
  tone,
  action,
}: {
  icon: Icon | null;
  label: string;
  description: string | null;
  trailing: { kind: "hint"; text: string } | { kind: "check" } | null;
  tone: "default" | "danger";
  action:
    | { kind: "button"; onClick: () => void }
    | { kind: "link"; to: string; onClick: () => void }
    | { kind: "disabled" };
}): React.JSX.Element {
  const className = cn(
    description === null ? "items-center" : "items-start",
    "flex w-full gap-2.5 rounded-lg border-none bg-transparent px-2.5 py-2 text-left font-dm-sans text-sm font-medium no-underline",
    action.kind === "disabled"
      ? "cursor-not-allowed text-ds-slate-400"
      : cn(
          "cursor-pointer hover:bg-ds-slate-100 focus-visible:bg-ds-slate-100 focus-visible:outline-none",
          tone === "danger" ? "text-ds-strawberry-700" : "text-ds-slate-950"
        )
  );
  const RowIcon = icon;
  const content = (
    <>
      {RowIcon === null ? null : description === null ? (
        <RowIcon className="size-4 shrink-0" stroke={1.5} />
      ) : (
        // On the label's line, not centred on the two lines.
        <span className="flex h-5 shrink-0 items-center">
          <RowIcon className="size-4" stroke={1.5} />
        </span>
      )}
      {description === null ? (
        <span className="min-w-0 flex-1">{label}</span>
      ) : (
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span>{label}</span>
          <span className="text-caption font-normal text-ds-slate-500">
            {description}
          </span>
        </span>
      )}
      {trailing === null ? null : trailing.kind === "hint" ? (
        <span className="shrink-0 text-desktop-meta text-ds-slate-500">
          {trailing.text}
        </span>
      ) : (
        <IconCheck className="size-4 shrink-0" stroke={1.5} />
      )}
    </>
  );
  switch (action.kind) {
    case "button":
      return (
        <button
          type="button"
          role="menuitem"
          className={className}
          onClick={action.onClick}
        >
          {content}
        </button>
      );
    case "link":
      return (
        <Link
          to={action.to}
          role="menuitem"
          className={className}
          onClick={action.onClick}
        >
          {content}
        </Link>
      );
    case "disabled":
      return (
        <button type="button" role="menuitem" className={className} disabled>
          {content}
        </button>
      );
    default:
      throw new Error(action satisfies never);
  }
}

/**
 * One option of a menu that picks a value (Figma "Sous-menu · Language"): the
 * check of the value in use leads the row, and `hint` ends it.
 */
export function DesktopMenuRadioItem({
  label,
  hint,
  checked,
  onClick,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={checked}
      onClick={onClick}
      className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg border-none bg-transparent px-2.5 py-2 text-left font-dm-sans hover:bg-ds-slate-100 focus-visible:bg-ds-slate-100 focus-visible:outline-none"
    >
      <span className="flex size-4 shrink-0 text-ds-slate-950">
        {checked ? <IconCheck className="size-4" stroke={1.5} /> : null}
      </span>
      <span
        className={cn(
          "min-w-0 flex-1 text-sm text-ds-slate-950",
          checked ? "font-semibold" : "font-medium"
        )}
      >
        {label}
      </span>
      <span className="shrink-0 text-desktop-meta text-ds-slate-700">
        {hint}
      </span>
    </button>
  );
}

/** The line between two groups of rows. */
export function DesktopMenuDivider(): React.JSX.Element {
  return <div className="h-px shrink-0 bg-ds-slate-100" />;
}

// The gap between a menu and the submenu it opens (Figma "Sous-menu · Language").
const SUBMENU_GAP_PX = 6;

/**
 * A row that opens a submenu beside its menu (Figma "Sous-menu · Language"):
 * the card hangs off the menu's right edge, bottoms aligned, and closes with
 * it. A second click on the row closes it.
 *
 * Portalled rather than nested: the menu card clips what overflows it. The
 * keyboard follows: ArrowRight on the row opens it on the checked option (or
 * the first), the arrows move inside it, Escape and ArrowLeft hand the focus
 * back to the row, and Tab closes the whole menu as the parent menu does.
 */
export function DesktopSubmenu({
  icon,
  label,
  hint,
  className,
  children,
}: {
  icon: Icon;
  label: string;
  /** The current value, before the chevron. */
  hint: string;
  /** The submenu card's width. */
  className: string;
  children: ReactNode;
}): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const rowRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{
    horizontal:
      { edge: "left"; left: number } | { edge: "right"; right: number };
    bottom: number;
  } | null>(null);

  // Layout effect: placed before the first paint, from the menu card it hangs
  // off (the closest `role="menu"` around the row). It opens on the side with
  // room for it: after the menu, or before it when the menu sits near the
  // right edge (the right-to-left sidebar in Arabic).
  useLayoutEffect(() => {
    if (!isOpen) return;
    const row = rowRef.current;
    const card = cardRef.current;
    if (row === null || card === null)
      throw new Error("should not happen: an open submenu with no row or card");
    const menu = row.closest('[role="menu"]');
    if (menu === null)
      throw new Error("should not happen: a submenu outside a menu");
    const rect = menu.getBoundingClientRect();
    const { width } = card.getBoundingClientRect();
    // `clientWidth`, as the popover: a fixed `right` excludes the scrollbar.
    const viewport = document.documentElement.clientWidth;
    const fitsAfter = rect.right + SUBMENU_GAP_PX + width <= viewport;
    setPlace({
      horizontal: fitsAfter
        ? { edge: "left", left: rect.right + SUBMENU_GAP_PX }
        : { edge: "right", right: viewport - rect.left + SUBMENU_GAP_PX },
      bottom: window.innerHeight - rect.bottom,
    });
  }, [isOpen]);

  // Capture phase: the parent menu listens on the document too, and its arrows
  // only know its own rows. Inside the submenu these keys are the submenu's.
  useEffect(() => {
    if (!isOpen) return;
    const card = cardRef.current;
    if (card === null) throw new Error("should not happen: no submenu card");
    const items = [
      ...card.querySelectorAll<HTMLButtonElement>(
        '[role="menuitemradio"], [role="menuitem"]'
      ),
    ];
    const checked = card.querySelector<HTMLButtonElement>(
      '[aria-checked="true"]'
    );
    const first = checked === null ? items[0] : checked;
    if (first !== undefined) first.focus();

    function handleKeyDown(event: KeyboardEvent): void {
      const focused = document.activeElement;
      if (card === null || focused === null || !card.contains(focused)) return;
      if (event.key === "Escape" || event.key === "ArrowLeft") {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(false);
        const row = rowRef.current;
        if (row !== null) row.focus();
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      event.preventDefault();
      event.stopPropagation();
      const current = items.findIndex((item) => item === focused);
      const step = event.key === "ArrowDown" ? 1 : -1;
      const next =
        current === -1 ? 0 : (current + step + items.length) % items.length;
      const item = items[next];
      if (item === undefined)
        throw new Error(
          `Invariant: submenu index ${String(next)} out of range`
        );
      item.focus();
    }

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isOpen]);

  const RowIcon = icon;
  return (
    <>
      <button
        ref={rowRef}
        type="button"
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        onKeyDown={(event) => {
          if (event.key !== "ArrowRight") return;
          event.preventDefault();
          setIsOpen(true);
        }}
        className={cn(
          "flex w-full cursor-pointer items-center gap-2.5 rounded-lg border-none px-2.5 py-2 text-left font-dm-sans text-sm font-medium text-ds-slate-950 hover:bg-ds-slate-100 focus-visible:bg-ds-slate-100 focus-visible:outline-none",
          isOpen ? "bg-ds-slate-100" : "bg-transparent"
        )}
      >
        <RowIcon className="size-4 shrink-0" stroke={1.5} />
        <span className="min-w-0 flex-1">{label}</span>
        <span className="shrink-0 text-desktop-meta text-ds-slate-500">
          {hint} ›
        </span>
      </button>
      {isOpen
        ? createPortal(
            <div
              ref={cardRef}
              role="menu"
              aria-label={label}
              style={
                place === null
                  ? { position: "fixed", top: 0, left: 0, opacity: 0 }
                  : {
                      position: "fixed",
                      bottom: place.bottom,
                      ...(place.horizontal.edge === "left"
                        ? { left: place.horizontal.left }
                        : { right: place.horizontal.right }),
                    }
              }
              className={cn(
                POPOVER_CARD_CLASS,
                "gap-0.5 rounded-xl border-ds-slate-100 p-1.5",
                className
              )}
            >
              {children}
            </div>,
            document.body
          )
        : null}
    </>
  );
}
